'use client'

import { useEffect, useRef, useState } from "react"
import { AnimatePresence, motion, useInView } from "framer-motion"
import { BatteryFull, Camera, Flashlight, Loader2, Mail, MessageSquareText, Signal, Wifi } from "lucide-react"

import { cn } from "@/lib/util"
import { useMotionAllowed } from "./motion"

interface Scene {
  id: string
  label: string
  path: string
  body: string
  status: number
  response: Array<[string, string | number]>
  notice: Notice
}

// What appears on the recipient's lock screen.
interface Notice {
  channel: 'sms' | 'email'
  title: string
  text: string
  // A second, quieter line: the start of an email's body.
  detail?: string
}

// The three things the API does, each as a request and what the recipient sees. The requests
// and answers are the ones in the docs (lib/docs/content.ts).
function scenesOf(smsCredits: number, emailCredits: number): Scene[] {
  return [
    {
      id: 'verify',
      label: 'Баталгаажуулах код',
      path: '/v1/verify',
      body: '{\n  "to": "99112233",\n  "brand": "Shop.mn"\n}',
      status: 200,
      response: [['status', 'pending'], ['channel', 'sms'], ['credits', smsCredits]],
      notice: { channel: 'sms', title: 'Мессеж', text: 'Shop.mn: Таны баталгаажуулах код 482913' },
    },
    {
      id: 'sms',
      label: 'Мэдэгдэл',
      path: '/v1/sms',
      body: '{\n  "to": "99112233",\n  "text": "Tanii zahialga batalgaajlaa. #1042"\n}',
      status: 200,
      response: [['status', 'sent'], ['parts', 1], ['credits', smsCredits]],
      notice: { channel: 'sms', title: 'Мессеж', text: 'Tanii zahialga batalgaajlaa. #1042' },
    },
    {
      id: 'email',
      label: 'Имэйл',
      path: '/v1/email',
      body: '{\n  "to": "bat@example.com",\n  "subject": "Захиалга баталгаажлаа",\n  "html": "<p>Баярлалаа!</p>"\n}',
      status: 202,
      response: [['status', 'queued'], ['channel', 'email'], ['credits', emailCredits]],
      notice: { channel: 'email', title: 'Shop.mn', text: 'Захиалга баталгаажлаа', detail: 'Баярлалаа!' },
    },
  ]
}

type Phase = 'typing' | 'sending' | 'done'

// The hero: a request is typed, answered, and the message lands on a phone. It plays by itself
// and a visitor can pick a scene.
export default function HeroDemo({ smsCredits, emailCredits }: { smsCredits: number, emailCredits: number }) {
  const scenes = scenesOf(smsCredits, emailCredits)
  const allowed = useMotionAllowed()
  // It stands still once it has left the screen: nobody is watching it, and a visitor reading
  // further down is not disturbed by it.
  const ref = useRef<HTMLDivElement>(null)
  const playing = useInView(ref) && allowed
  const [index, setIndex] = useState(0)
  const [typed, setTyped] = useState(0)
  const [phase, setPhase] = useState<Phase>('typing')
  // The messages on the phone, newest first. A phone keeps what it received, so an earlier
  // message stays under the new one.
  const [arrived, setArrived] = useState<Array<{ key: number, notice: Notice }>>([])

  const scene = scenes[index]
  const length = scene.body.length

  const select = (next: number) => {
    setIndex(next)
    setTyped(0)
    setPhase('typing')
  }

  useEffect(() => {
    if(!playing) return
    let timer: ReturnType<typeof setTimeout>
    if(phase === 'typing') {
      timer = typed < length
        ? setTimeout(() => setTyped(count => Math.min(length, count + 2)), 26)
        : setTimeout(() => setPhase('sending'), 300)
    }
    else if(phase === 'sending') {
      timer = setTimeout(() => {
        setPhase('done')
        setArrived(list => [{ key: (list[0]?.key ?? 0) + 1, notice: scene.notice }, ...list].slice(0, 2))
      }, 700)
    }
    else timer = setTimeout(() => select((index + 1) % scenes.length), 4200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, phase, typed, index, length])

  // With motion off, each scene is shown finished.
  const body = allowed ? scene.body.slice(0, typed) : scene.body
  const done = !allowed || phase === 'done'
  const notices = allowed ? arrived : [{ key: index, notice: scene.notice }]

  return (
    <div ref={ref}>
      <p className="sr-only">Жишээ: API хүсэлт илгээхэд хүлээн авагчийн утсанд мессеж очно.</p>
      <div role="tablist" aria-label="Жишээ" className="relative isolate flex flex-wrap justify-center gap-1 lg:justify-end">
        {scenes.map((item, position) => (
          <button key={item.id} type="button" role="tab" aria-selected={position === index} onClick={() => select(position)}
            className={cn("relative cursor-pointer rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              position === index ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}>
            {position === index && <motion.span layoutId="hero-scene" className="absolute inset-0 -z-10 rounded-full bg-muted" transition={{ type: 'spring', stiffness: 400, damping: 34 }} />}
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col items-center sm:flex-row sm:items-start sm:justify-center lg:justify-end">
        {/* The request and its answer. */}
        <div className="relative z-10 w-full max-w-[400px] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 text-zinc-100 shadow-2xl shadow-blue-950/20 sm:mt-20 sm:w-[340px] lg:w-[320px] xl:w-[400px] dark:bg-zinc-900">
          <div className="flex items-center gap-2.5 border-b border-white/10 px-4 py-3">
            <span className="rounded-md bg-emerald-400/10 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-400">POST</span>
            <span className="text-[13px] text-zinc-300">{scene.path}</span>
          </div>

          {/* Every scene's whole request lies unseen in the same cell as the one being typed, so
              the pane is always as tall as the longest of them, however the lines wrap. The card
              keeps one height, and on a phone nothing under it moves as a scene plays or changes. */}
          <div aria-hidden className="grid grid-cols-1 px-4 py-3.5 text-xs leading-6">
            {scenes.map(item => (
              <pre key={item.id} className="invisible col-start-1 row-start-1 min-w-0 whitespace-pre-wrap break-words font-inter">
                <JsonLines text={item.body} /><span className="ml-px inline-block w-[6px]" />
              </pre>
            ))}
            <pre className="col-start-1 row-start-1 min-w-0 whitespace-pre-wrap break-words font-inter">
              <JsonLines text={body} />
              {allowed && phase === 'typing' && <span className="ml-px inline-block h-3.5 w-[6px] translate-y-0.5 animate-pulse bg-blue-400" />}
            </pre>
          </div>

          {/* Tall enough for the answer before it is there: its five lines under the heading. */}
          <div className="min-h-[176px] border-t border-white/10 bg-white/[0.03] px-4 py-3.5">
            <div className="flex h-5 items-center justify-between text-xs">
              <span className="font-medium text-zinc-400">Хариу</span>
              {phase === 'sending' && allowed && (
                <span className="flex items-center gap-1.5 text-zinc-400"><Loader2 className="size-3.5 animate-spin" />Илгээж байна</span>
              )}
              {done && (
                <span className="flex items-center gap-1.5 font-semibold tabular-nums text-emerald-400"><span className="size-1.5 rounded-full bg-emerald-400" />{scene.status}</span>
              )}
            </div>
            <AnimatePresence mode="wait">
              {done && (
                <motion.pre key={scene.id} aria-hidden initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="mt-2 font-inter text-xs leading-6">
                  <JsonLines text={`{\n${scene.response.map(([key, value]) => `  "${key}": ${JSON.stringify(value)}`).join(',\n')}\n}`} />
                </motion.pre>
              )}
            </AnimatePresence>
          </div>
        </div>

        <Phone notices={notices} />
      </div>
    </div>
  )
}

// JSON, coloured line by line. A line still being typed stays plain until it is whole.
// The text is set in the page's own font, where a space is too narrow to show a level of JSON.
// So each line's indent is drawn as a gap of a fixed width instead.
function JsonLines({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, position, lines) => {
        const indent = line.length - line.trimStart().length
        const pair = line.slice(indent).match(/^("[^"]*")(:\s*)(.*)$/)
        return (
          <span key={position}>
            {indent > 0 && <span className="inline-block" style={{ width: `${indent * 0.6}em` }} />}
            {pair ? (
              <>
                <span className="text-sky-300">{pair[1]}</span><span className="text-zinc-500">{pair[2]}</span>
                <span className={pair[3].startsWith('"') ? 'text-emerald-300' : 'text-amber-300'}>{pair[3]}</span>
              </>
            ) : <span className="text-zinc-400">{line.slice(indent)}</span>}
            {position < lines.length - 1 && '\n'}
          </span>
        )
      })}
    </>
  )
}

const WEEKDAYS = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба']

// The recipient's handset, showing its lock screen. On a phone-width page only its top shows.
function Phone({ notices }: { notices: Array<{ key: number, notice: Notice }> }) {
  // Today's date is read in the browser, so the server's text and the first paint agree.
  const [today, setToday] = useState('')

  useEffect(() => {
    const now = new Date()
    setToday(`${now.getMonth() + 1} сарын ${now.getDate()}, ${WEEKDAYS[now.getDay()]}`)
  }, [])

  return (
    <div aria-hidden className="relative mt-6 w-[248px] shrink-0 px-1 max-sm:h-[350px] max-sm:overflow-hidden max-sm:[mask-image:linear-gradient(to_bottom,black_78%,transparent)] sm:-ml-10 sm:mt-0">
      <div className="relative">
        {/* The buttons on the sides of the frame. */}
        <span className="absolute -left-[3px] top-[88px] h-6 w-[3px] rounded-l-sm bg-zinc-600" />
        <span className="absolute -left-[3px] top-[128px] h-11 w-[3px] rounded-l-sm bg-zinc-600" />
        <span className="absolute -left-[3px] top-[182px] h-11 w-[3px] rounded-l-sm bg-zinc-600" />
        <span className="absolute -right-[3px] top-[148px] h-16 w-[3px] rounded-r-sm bg-zinc-600" />

        <div className="rounded-[2.75rem] bg-linear-to-b from-zinc-500 via-zinc-700 to-zinc-600 p-[2px] shadow-2xl shadow-blue-950/40">
          <div className="rounded-[2.65rem] bg-black p-[7px]">
            <div className="relative h-[468px] overflow-hidden rounded-[2.2rem] bg-linear-to-b from-blue-500 via-blue-500 to-indigo-500">
              {/* The wallpaper: the brand colours with some depth. */}
              <div className="absolute -left-16 -top-10 size-56 rounded-full bg-sky-300/50 blur-3xl" />
              <div className="absolute -bottom-16 -right-14 size-64 rounded-full bg-indigo-700/70 blur-3xl" />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-black/25 to-transparent" />

              <div className="relative flex h-full flex-col text-white">
                <div className="flex h-11 items-center justify-between px-6 pt-1">
                  <Signal className="size-3.5" strokeWidth={2.5} />
                  <span className="absolute left-1/2 top-2.5 h-[22px] w-[76px] -translate-x-1/2 rounded-full bg-black" />
                  <span className="flex items-center gap-1">
                    <Wifi className="size-3.5" strokeWidth={2.5} />
                    <BatteryFull className="size-[18px]" strokeWidth={2} />
                  </span>
                </div>

                <p className="mt-3 h-5 text-center text-[13px] font-medium leading-5 text-white/90">{today}</p>
                <p className="text-center font-gilroy text-[68px] font-semibold leading-[1.05] tracking-tight tabular-nums">9:41</p>

                <div className="relative mt-4 flex flex-col gap-2 px-2.5">
                  <AnimatePresence initial={false} mode="popLayout">
                    {notices.map((item, position) => (
                      <motion.div key={item.key} layout
                        initial={{ opacity: 0, y: -28, scale: 0.9 }}
                        animate={{ opacity: position === 0 ? 1 : 0.55, y: 0, scale: position === 0 ? 1 : 0.95 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 360, damping: 30 }}
                        className="flex items-center gap-2.5 rounded-[1.25rem] bg-white/80 p-2.5 pr-3 text-zinc-900 shadow-lg shadow-black/15 backdrop-blur-xl">
                        <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-linear-to-b text-white shadow-sm",
                          item.notice.channel === 'sms' ? 'from-emerald-400 to-emerald-500' : 'from-sky-400 to-blue-500')}>
                          {item.notice.channel === 'sms' ? <MessageSquareText className="size-[18px]" /> : <Mail className="size-[18px]" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[13px] font-semibold leading-4">{item.notice.title}</span>
                            <span className="shrink-0 text-[11px] leading-4 text-zinc-500">{position === 0 ? 'одоо' : 'саяхан'}</span>
                          </span>
                          <span className="mt-0.5 block text-[12.5px] leading-[1.3]">{item.notice.text}</span>
                          {item.notice.detail && <span className="block truncate text-[12.5px] leading-[1.3] text-zinc-500">{item.notice.detail}</span>}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                <div className="mt-auto flex items-center justify-between px-7 pb-8">
                  <span className="flex size-10 items-center justify-center rounded-full bg-black/25 backdrop-blur-md"><Flashlight className="size-[18px]" /></span>
                  <span className="flex size-10 items-center justify-center rounded-full bg-black/25 backdrop-blur-md"><Camera className="size-[18px]" /></span>
                </div>
                <span className="absolute bottom-2 left-1/2 h-1 w-24 -translate-x-1/2 rounded-full bg-white/90" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
